'use client';

import React, {
  createContext,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger';
import { useIsomorphicLayoutEffect } from 'hooks/dom/useIsomorphicLayoutEffect';
import { createPortal } from 'react-dom';
import dynamic from 'next/dynamic';
import {
  TimelineData,
  TimelineSection,
  TimelineSettings,
} from 'utils/masterTimelineTypes';

// Dev-only animation debugger. Code-split + dev-gated so the 1100+ line overlay
// and its deps are tree-shaken out of production bundles (NODE_ENV folds at
// build time). The render site below is also gated, so this is purely a
// bundling optimization — every page using MasterTimelineProvider stops
// shipping the debugger to end users.
const AnimationTimelineOverlay: React.ComponentType<any> =
  process.env.NODE_ENV === 'production'
    ? () => null
    : dynamic(() => import('components/devtools/AnimationTimelineOverlay'), {
        ssr: false,
      });

if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger);
}

type GSAPTimeline = gsap.core.Timeline;

export type { TimelineData, TimelineSection, TimelineSettings };

// Context type
type MasterTimelineContextType = {
  master: GSAPTimeline | null;
  add: (tl: GSAPTimeline, label?: string, position?: number) => void;
  schedule: (fn: () => void, label?: string) => void;
  getSettings: () => TimelineSettings;
  updateSettings: (settings: Partial<TimelineSettings>) => void;
};

export const MasterTimelineContext = createContext<MasterTimelineContextType>({
  master: null,
  add: () => {
    // No-op in default context
  },
  schedule: () => {
    // No-op in default context
  },
  getSettings: () => ({
    updateInterval: 80,
    showNested: true,
    height: 'medium',
    showLabels: true,
    opacity: 0.9,
    enabled: true,
  }),
  updateSettings: () => {
    // No-op in default context
  },
});

// LocalStorage key
const SETTINGS_STORAGE_KEY = 'ultrahuman-timeline-settings';

// Load settings from localStorage
const loadSettings = (): TimelineSettings => {
  if (typeof window === 'undefined') {
    return {
      updateInterval: 80,
      showNested: true,
      height: 'medium',
      showLabels: true,
      opacity: 0.9,
      enabled: true,
    };
  }

  try {
    const stored = localStorage.getItem(SETTINGS_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return {
        updateInterval: parsed.updateInterval ?? 80,
        showNested: parsed.showNested ?? true,
        height: parsed.height ?? 'medium',
        showLabels: parsed.showLabels ?? true,
        opacity: parsed.opacity ?? 0.9,
        enabled: parsed.enabled ?? true,
      };
    }
  } catch (error) {
    console.warn('Failed to load timeline settings:', error);
  }

  return {
    updateInterval: 80,
    showNested: true,
    height: 'medium',
    showLabels: true,
    opacity: 0.9,
    enabled: true,
  };
};

// Save settings to localStorage
const saveSettings = (settings: TimelineSettings) => {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(settings));
  } catch (error) {
    console.warn('Failed to save timeline settings:', error);
  }
};

export const MasterTimelineProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  // Master timeline ref
  const masterRef = useRef<GSAPTimeline | null>(null);
  const timelineOrderRef = useRef<number>(0);

  // Map to store timeline references for tracking
  const sectionTimelineMapRef = useRef<Map<string, GSAPTimeline>>(new Map());

  // Data ref - stores all timeline data without causing re-renders
  const dataRef = useRef<TimelineData>({
    time: 0,
    duration: 0,
    sections: [],
  });

  // Settings ref for performance (avoid re-renders on every setting change)
  const settingsRef = useRef<TimelineSettings>(loadSettings());

  // Update interval ref
  const updateIntervalIdRef = useRef<NodeJS.Timeout | null>(null);

  // Callback for notifying overlay of data changes
  const onDataChangeCallbackRef = useRef<((data: TimelineData) => void) | null>(
    null,
  );

  // Check if timeline should be enabled
  const isDevTimelineEnabled =
    String(
      process.env.NEXT_PUBLIC_SHOW_ANIMATION_TIMELINE || '',
    ).toLowerCase() === 'true';
  const isDev = process.env.NODE_ENV === 'development';
  const shouldEnable = isDevTimelineEnabled && isDev;

  // Mounted state for portal rendering
  const [mounted, setMounted] = React.useState(false);

  // Optimized batched update function
  const batchedUpdate = useCallback(() => {
    if (!masterRef.current || !shouldEnable || !settingsRef.current.enabled)
      return;

    const master = masterRef.current;
    const currentTime = master.time();
    const currentDuration = master.duration();

    // Track if any changes occurred
    let hasChanges = false;

    // Update global time and duration
    if (
      dataRef.current.time !== currentTime ||
      dataRef.current.duration !== currentDuration
    ) {
      dataRef.current.time = currentTime;
      dataRef.current.duration = currentDuration;
      hasChanges = true;
    }

    // Update section progress efficiently
    const sections = dataRef.current.sections;
    for (let i = 0; i < sections.length; i++) {
      const section = sections[i];
      const tl = sectionTimelineMapRef.current.get(section.id);

      if (!tl) continue;

      const progress = typeof tl.progress === 'function' ? tl.progress() : 0;
      const duration = typeof tl.duration === 'function' ? tl.duration() : 0;
      const end = section.start + (isFinite(duration) ? duration : 0);

      // Only update if values changed
      if (
        section.progress !== progress ||
        section.duration !== duration ||
        section.end !== end
      ) {
        section.progress = progress;
        section.duration = duration;
        section.end = end;
        hasChanges = true;
      }
    }

    // Notify overlay if changes occurred
    if (hasChanges && onDataChangeCallbackRef.current) {
      onDataChangeCallbackRef.current(dataRef.current);
    }
  }, [shouldEnable]);

  // Get current settings
  const getSettings = useCallback((): TimelineSettings => {
    return { ...settingsRef.current };
  }, []);

  // Update settings
  const updateSettings = useCallback(
    (newSettings: Partial<TimelineSettings>) => {
      const updated = { ...settingsRef.current, ...newSettings };
      settingsRef.current = updated;
      saveSettings(updated);

      // Restart update interval if interval changed
      if (newSettings.updateInterval !== undefined) {
        if (updateIntervalIdRef.current) {
          clearInterval(updateIntervalIdRef.current);
        }
        if (updated.enabled && shouldEnable) {
          updateIntervalIdRef.current = setInterval(
            batchedUpdate,
            updated.updateInterval,
          );
        }
      }

      // Toggle interval if enabled changed
      if (newSettings.enabled !== undefined) {
        if (updateIntervalIdRef.current) {
          clearInterval(updateIntervalIdRef.current);
          updateIntervalIdRef.current = null;
        }
        if (updated.enabled && shouldEnable) {
          updateIntervalIdRef.current = setInterval(
            batchedUpdate,
            updated.updateInterval,
          );
        }
      }
    },
    [batchedUpdate, shouldEnable],
  );

  // Initialize master timeline
  useIsomorphicLayoutEffect(() => {
    masterRef.current = gsap.timeline({ paused: true });
    timelineOrderRef.current = 0;

    // Start batched update cycle if enabled
    if (shouldEnable && settingsRef.current.enabled) {
      updateIntervalIdRef.current = setInterval(
        batchedUpdate,
        settingsRef.current.updateInterval,
      );
    }

    // Refresh ScrollTrigger after layout settles
    const raf = requestAnimationFrame(() => ScrollTrigger.refresh());

    return () => {
      // Cleanup
      if (masterRef.current) {
        masterRef.current.kill();
        masterRef.current = null;
      }
      if (updateIntervalIdRef.current) {
        clearInterval(updateIntervalIdRef.current);
        updateIntervalIdRef.current = null;
      }
      sectionTimelineMapRef.current.clear();
      timelineOrderRef.current = 0;
      cancelAnimationFrame(raf);
    };
  }, [batchedUpdate, shouldEnable]);

  // Set mounted state
  React.useEffect(() => {
    setMounted(true);
  }, []);

  // Add timeline to master
  const add = useCallback(
    (tl: GSAPTimeline, label?: string, positionOverride?: number) => {
      if (!masterRef.current || !tl) return;

      const timelinePosition =
        typeof positionOverride === 'number' && isFinite(positionOverride)
          ? positionOverride
          : timelineOrderRef.current;
      const finalLabel = label || `timeline-${timelineOrderRef.current}`;
      const hasCustomLabel = !!label; // Track if user provided a label

      // Add to master timeline
      masterRef.current.addLabel(finalLabel, timelinePosition);
      masterRef.current.add(tl, timelinePosition);

      timelineOrderRef.current += 1;

      // Register for tracking if enabled
      if (shouldEnable) {
        // If this has a custom label, remove any existing section with the same label
        if (hasCustomLabel) {
          const existingIndex = dataRef.current.sections.findIndex(
            (s) => s.label === finalLabel,
          );
          if (existingIndex !== -1) {
            const oldSection = dataRef.current.sections[existingIndex];
            // Remove old section and its timeline reference
            dataRef.current.sections.splice(existingIndex, 1);
            sectionTimelineMapRef.current.delete(oldSection.id);
          }
        }

        const duration = typeof tl.duration === 'function' ? tl.duration() : 0;
        const newSection: TimelineSection = {
          id: finalLabel,
          label: finalLabel,
          start: timelinePosition,
          end: timelinePosition + (isFinite(duration) ? duration : 0),
          duration: isFinite(duration) ? duration : 0,
          isCallback: false,
          progress: 0,
        };

        dataRef.current.sections.push(newSection);
        sectionTimelineMapRef.current.set(finalLabel, tl);

        // Update duration
        if (masterRef.current) {
          dataRef.current.duration = masterRef.current.duration();
        }

        // Notify overlay of new section
        if (onDataChangeCallbackRef.current) {
          onDataChangeCallbackRef.current(dataRef.current);
        }
      }

      ScrollTrigger.refresh();
    },
    [shouldEnable],
  );

  // Schedule callback on master timeline
  const schedule = useCallback(
    (fn: () => void, label?: string) => {
      if (!masterRef.current || typeof fn !== 'function') return;

      const timelinePosition = timelineOrderRef.current;
      const finalLabel = label || `callback-${timelineOrderRef.current}`;
      const hasCustomLabel = !!label; // Track if user provided a label

      // Add to master timeline
      masterRef.current.addLabel(finalLabel, timelinePosition);
      masterRef.current.add(fn, timelinePosition);

      timelineOrderRef.current += 1;

      // Register for tracking if enabled (callbacks won't be displayed)
      if (shouldEnable) {
        // If this has a custom label, remove any existing callback with the same label
        if (hasCustomLabel) {
          const existingIndex = dataRef.current.sections.findIndex(
            (s) => s.label === finalLabel,
          );
          if (existingIndex !== -1) {
            // Remove old callback section
            dataRef.current.sections.splice(existingIndex, 1);
          }
        }

        const newSection: TimelineSection = {
          id: finalLabel,
          label: finalLabel,
          start: timelinePosition,
          end: timelinePosition,
          duration: 0,
          isCallback: true,
          progress: 0,
        };

        dataRef.current.sections.push(newSection);

        // Update duration
        if (masterRef.current) {
          dataRef.current.duration = masterRef.current.duration();
        }

        // Notify overlay of new section
        if (onDataChangeCallbackRef.current) {
          onDataChangeCallbackRef.current(dataRef.current);
        }
      }

      ScrollTrigger.refresh();
    },
    [shouldEnable],
  );

  // Get nested timeline sections for introspection
  const getNestedTimelineSections = useCallback(
    (
      sectionId: string,
      parentSectionId?: string,
      childStartTime?: number,
    ): TimelineData | null => {
      if (!shouldEnable) return null;

      let targetTimeline: any = null;

      // Handle nested drilling (child of child)
      if (parentSectionId && typeof childStartTime === 'number') {
        const parentTl = sectionTimelineMapRef.current.get(parentSectionId);
        if (!parentTl) return null;

        try {
          const children = parentTl.getChildren
            ? parentTl.getChildren(false)
            : [];
          const child = children.find((c: any) => {
            if (typeof c === 'function') return false;
            const cStartTime =
              typeof c.startTime === 'function'
                ? c.startTime()
                : c._startTime || 0;
            return Math.abs(cStartTime - childStartTime) < 0.01;
          });

          if (
            child &&
            (typeof (child as any).getChildren === 'function' ||
              (child as any)._labels)
          ) {
            targetTimeline = child;
          } else {
            return null;
          }
        } catch (error) {
          return null;
        }
      } else {
        // Get direct nested timeline
        targetTimeline = sectionTimelineMapRef.current.get(sectionId);
        if (!targetTimeline) return null;
      }

      try {
        // Get children from timeline
        let children: any[] = [];
        if (targetTimeline.getChildren) {
          children = targetTimeline.getChildren(true) || [];
          if (children.length === 0) {
            children = targetTimeline.getChildren(false) || [];
          }
        }

        if (!children || children.length === 0) return null;

        const nestedSections: TimelineSection[] = [];
        let nestedMaxEnd = 0;

        // Build nested sections
        children.forEach((child: any, index: number) => {
          if (typeof child === 'function') return;

          const startTime =
            typeof child.startTime === 'function'
              ? child.startTime()
              : child._startTime || 0;
          const duration =
            typeof child.duration === 'function'
              ? child.duration()
              : child._duration || 0;
          const progress =
            typeof child.progress === 'function' ? child.progress() : 0;

          // Skip zero-duration items without labels (but allow very small durations)
          if (duration < 0.001 && !child.vars?.label && !child.vars?.id) return;

          // Determine label
          let label = `child-${index}`;
          if (child.vars?.label) {
            label = child.vars.label;
          } else if (child.vars?.id) {
            label = child.vars.id;
          }

          const end = startTime + duration;
          nestedMaxEnd = Math.max(nestedMaxEnd, end);

          nestedSections.push({
            id: `${sectionId}-nested-${index}-${label}`,
            label,
            start: startTime,
            end,
            duration,
            isCallback: false,
            progress,
          });
        });

        if (nestedSections.length === 0) {
          return null;
        }

        const nestedDuration = nestedMaxEnd || targetTimeline.duration();
        const nestedTime = targetTimeline.time();

        return {
          time: nestedTime,
          duration: nestedDuration,
          sections: nestedSections.sort((a, b) => {
            if (a.start !== b.start) return a.start - b.start;
            if (a.end !== b.end) return a.end - b.end;
            return a.label.localeCompare(b.label);
          }),
        };
      } catch (error) {
        return null;
      }
    },
    [shouldEnable],
  );

  // Register data change callback
  const registerDataChangeCallback = useCallback(
    (callback: (data: TimelineData) => void) => {
      onDataChangeCallbackRef.current = callback;
    },
    [],
  );

  // Context value - memoized to prevent unnecessary re-renders
  const contextValue = useMemo<MasterTimelineContextType>(
    () => ({
      master: masterRef.current,
      add,
      schedule,
      getSettings,
      updateSettings,
    }),
    [add, schedule, getSettings, updateSettings],
  );

  return (
    <MasterTimelineContext.Provider value={contextValue}>
      {children}
      {mounted && shouldEnable && typeof document !== 'undefined'
        ? createPortal(
            <AnimationTimelineOverlay
              dataRef={dataRef}
              settingsRef={settingsRef}
              onDataChange={registerDataChangeCallback}
              getNestedTimelineSections={getNestedTimelineSections}
              updateSettings={updateSettings}
            />,
            document.body,
          )
        : null}
    </MasterTimelineContext.Provider>
  );
};

